import React, {useEffect, useState} from 'react'
import styled, {css} from 'styled-components'
import {colors} from '../sharedComponents/colors'
import {useAppState} from '../state'
import IsUserHealthInspector from '../hooks/userIsHealthInspector'
import {useTranslation} from 'react-i18next'
import {v4 as uuid} from 'uuid'
import {NotificationType} from '../state/state'
import {ERROR_NOTIFICATION_DURATION_MS} from '../pages/appliances/appliancesUtils'
import {Alarm, AlarmSubType, AlarmType} from '../state/alarms/state'
import {TFunction} from 'i18next'
import {
  alarmIconPicker,
  alarmLimitUnit,
  alarmNameFormatter,
  alarmSubtypeToTranslation,
  alarmUnit,
  alarmValueFormatter,
  dateValueFormatter,
  Units
} from '../pages/alarms/formatters'
import {getDatetimeAgo} from '../Components/Atoms/Utils'
import {Link} from 'react-navi'
import LoadingWithPuff from '../Components/Atoms/LoadingWithPuff'
import {getIsBatteryAlarm} from '../state/alarms/utils'
import {TaskTypeEnum, UserLanguage, AssetData} from '../state/rest'
import AssetUploaderSmall from '../Components/Molecules/UploadImage'
import {SmartAsset} from '../Components/Atoms/ImageLoader'
import {IconButton} from '../Components/Atoms/Buttons'
import {IconCloseSmall} from '../Assets/Icons/TinyIcons'
import {Text as TextInfo} from '../Components/Atoms/Typography'
import InputWithTags from '../pages/tasks/GeneralTasks/InputWithTags'
import {getChefsteinTip} from '../pages/alarms/chefsteinTip'
import {
  ApplianceData,
  ApplianceDataValueAndUnit,
  Button,
  Input,
  Modal,
  ModalProps,
  SharedIcon,
  SmallIconPalette,
  Text,
  Title
} from '../sharedComponents/components'

export type ResolveAlarmModalFormData = {
  resolution: string
  resolvedBy: string
  followupTaskCreated?: boolean
}

interface ResolveAlarmModalProps extends ModalProps {
  alarmId?: string
  title: string
  showApplianceLink?: boolean
  onFormSubmit?: (formData: ResolveAlarmModalFormData) => void
  onSaveDescription?: (description: string) => void
}

export const ResolveAlarmModal: React.FC<ResolveAlarmModalProps> = ({
  isOpen,
  onClose,
  onFormSubmit,
  onSaveDescription,
  title,
  alarmId,
  showApplianceLink,
  ...rest
}) => {
  const {state, actions} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()
  const {t} = useTranslation(['appliances', 'common', 'alarms', 'tasks'])
  const selectedChain = state.selectedChainId ? state.chainsById[state.selectedChainId] : undefined
  const isResolvedByMandatoryForAllAlarms = selectedChain?.resolvedByMandatoryForAllAlarms ?? false
  const [formData, setFormData] = useState({resolution: '', resolvedBy: ''})
  const [submitting, setSubmitting] = useState(false)
  const [isChefsteinTipExpanded, setIsChefsteinTipExpanded] = useState(false)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [loadingTask, setLoadingTask] = useState<boolean>(false)
  const [alarm, setAlarm] = useState<Alarm | null>(null)
  const [followUpTaskCreated, setFollowUpTaskCreated] = useState<boolean | null>(alarm?.followupTaskCreated || false)

  const isSuperUser = state.me?.accessRights.superuser

  const units: Units = {
    temperature: state.site!.temperatureUnit,
    weight: state.site!.weightUnit
  }
  const site = state.site!
  const locale = state.site!.locale
  const language = state.me?.user.language || UserLanguage.en
  const actorSuggestions = site.actorSuggestions ?? []
  const hasActorSuggestions = actorSuggestions.length > 0
  let measuredValue = undefined
  let latestValue = undefined
  let unit = undefined
  let limitUnit = undefined

  if (alarm) {
    measuredValue = alarmValueFormatter(alarm.measuredValue, alarm.type, alarm.subtype, site)
    latestValue = alarm.latestValue
      ? alarmValueFormatter(alarm.latestValue.value, alarm.type, alarm.subtype, site)
      : undefined
    unit = alarmUnit(t, alarm.type, alarm.subtype, alarm.unit, units)
    limitUnit = alarmLimitUnit(t, alarm.type, alarm.subtype, alarm.unit, units)
  }

  useEffect(() => {
    const fn = async () => {
      if (alarmId) {
        setIsChefsteinTipExpanded(false)
        const alarm = await actions.v1.alarms.getAlarm({alarmId})
        setAlarm(alarm)
        setIsLoading(false)
        if (alarm) {
          setFormData({resolution: alarm.reason!!, resolvedBy: ''})
        }
      }
    }
    fn()
  }, [alarmId])

  const resetModal = () => {
    setFormData({resolution: '', resolvedBy: ''})
    setSubmitting(false)
    setIsLoading(true)
    setAlarm(null)
    setFollowUpTaskCreated(false)
    setIsChefsteinTipExpanded(false)
  }

  const handleButtonClick = async () => {
    setLoadingTask(true)
    const task = await actions.v1.performTask.getTask({taskId: alarm?.taskId!, siteId: state.site!.id!})
    let newEvent = {
      task: task,
      remarks: t('alarms:messages.followupremark', 'Follow-up task'),
      status: '',
      isFollowUp: true,
      ingredients: [''],
      suggestions: ['']
    }
    if (task) {
      if (task.taskType.id === TaskTypeEnum.WASTE) {
        newEvent.ingredients = alarm?.measurementTarget ? [alarm?.measurementTarget] : []
        newEvent.suggestions = []
        console.log('newEvent', newEvent)
      } else {
        newEvent.suggestions = alarm?.measurementTarget ? [alarm?.measurementTarget] : []
        newEvent.ingredients = []
      }
      const result = await actions.v1.performTask.createEvent(newEvent)
      const updatedAlarm = await actions.v1.alarms.updateAlarm({
        alarmId: alarmId,
        siteId: site.id,
        followupTaskCreated: true
      })
      setAlarm({...alarm!, followupTaskCreated: true})
      if (result) {
        setFollowUpTaskCreated(true)
      }
    }
    setLoadingTask(false)
  }

  const handleResolvedByChange = (resolvedBy: string) => {
    setFormData(prev => ({...prev, resolvedBy}))
  }

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement> & React.ChangeEvent<HTMLTextAreaElement>) => {
    switch (event.target.id) {
      case 'resolveAlarmModalFormReason':
        setFormData({...formData, resolution: event.target.value})
        break
      case 'resolveAlarmModalFormResolved':
        handleResolvedByChange(event.target.value)
        break
      default:
        break
    }
  }

  const onSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    if (!alarmId || !state.site?.id) {
      return
    }

    const hasNonEmptyResolvedBy = (formData.resolvedBy ?? '').trim().length > 0
    if (isResolvedByMandatoryForAllAlarms && !hasNonEmptyResolvedBy) {
      return
    }

    setSubmitting(true)
    try {
      // include modal-local followUpTaskCreated flag so parent can forward it to the backend
      await onFormSubmit?.({...formData, followupTaskCreated: followUpTaskCreated || false})
      onClose?.()
      resetModal()
    } catch (err) {
      actions.addNotification({
        id: uuid(),
        type: NotificationType.ERROR,
        title: t('appliances:messages.dataSendErrorTitle', 'Data send error'),
        description: t('appliances:messages.dataSendErrorDescription', 'Sending data failed. Please try again.'),
        visible: true,
        hideAfterDelay: ERROR_NOTIFICATION_DURATION_MS
      })
    }
    setSubmitting(false)
  }

  const onResetAndClose = () => {
    resetModal()
    onClose?.()
  }
  const isBatteryAlarm = getIsBatteryAlarm(alarm)
  const chefsteinTip = alarm ? getChefsteinTip(alarm) : null

  const getAlarmInstructions = () => {
    // TODO: show different instructions based in sensor type (different batteries)
    return (
      <>
        <Text size="M" color={colors.system.grey_50}>
          {t(
            `alarms:modal.instruction.description.battery`,
            `The sensor battery level is low. Change the battery to resolve this alarm.`
          )}
        </Text>
        <Title level={6} strong style={{margin: '0'}}>
          {t('alarms:modal.instruction.subTitle.battery', 'How to change the battery?')}
        </Title>
        <Title level={7} strong style={{margin: '0'}}>
          {t('alarms:modal.instruction.nokevalTitle.battery', 'Nokeval Sensors')}
        </Title>
        <Text data-cy="modal-alarm-instructions-self" size="M" color={colors.system.grey_50}>
          {t(
            'alarms:modal.instruction.nokevalBody.battery',
            `The battery cannot be replaced by the user. Please contact Chefstein customer support.`
          )}
        </Text>
        <Title level={7} strong style={{margin: '0'}}>
          {t('alarms:modal.instruction.whiteColdRoomTitle.battery', 'Chefstein white cold room sensors')}
        </Title>
        <Text data-cy="modal-alarm-instructions-self" size="M" color={colors.system.grey_50}>
          {t(
            'alarms:modal.instruction.whiteColdRoomBody.battery',
            `The batteries can be replaced by the user. You will need two 3.6V ER18505 AA batteries.
              Open the rear cover of the sensor, remove the old batteries, and install the new ones.`
          )}
        </Text>
        <Title level={7} strong style={{margin: '0'}}>
          {t('alarms:modal.instruction.coolingOrDishwasherTitle.battery', 'Chefstein cooling or dishwasher sensors')}
        </Title>
        <Text data-cy="modal-alarm-instructions-self" size="M" color={colors.system.grey_50}>
          {t(
            'alarms:modal.instruction.coolingOrDishwasherBody.battery',
            `Replacement batteries can be ordered from Chefstein customer support. You will need one EVE ER34615 D 3.6V battery.
              The battery can be replaced by the user. Open the casing, remove the old battery, and install the new one.`
          )}
        </Text>
        <Title level={7} strong style={{margin: '0'}}>
          {t('alarms:modal.instruction.wasteTitle.battery', 'Chefstein scale')}
        </Title>
        <Text data-cy="modal-alarm-instructions-self" size="M" color={colors.system.grey_50}>
          {t(
            'alarms:modal.instruction.wasteBody.battery',
            `Replacement batteries can be ordered from Chefstein customer support. You will need one EVE ER34615 D 3.6V battery.
The battery can be replaced by the user. Please refer to the separate instructions for battery replacement.`
          )}
        </Text>
        <br />
        <Text data-cy="modal-alarm-instructions-self" size="M" color={colors.system.grey_50}>
          {t(
            'alarms:modal.instruction.endInfo.battery',
            `All batteries can be purchased from Chefstein customer support.

If you are unsure which sensor you are using, please contact Chefstein customer support.`
          )}
        </Text>
      </>
    )
  }
  const handleAssetSaved = async (asset?: AssetData) => {
    if (!alarmId || !asset) return
    try {
      const updatedAlarm = await actions.v1.alarms.attachAlarmAsset({
        alarmId,
        assetId: asset.id,
        siteId: state.site!.id!
      })
      setAlarm(prev =>
        prev
          ? {...prev, reason: formData.resolution, resolvedBy: formData.resolvedBy, assets: updatedAlarm?.assets}
          : prev
      )
    } catch (err) {
      console.error('Error attaching asset to alarm', err)
    }
  }

  const handleRemoveAsset = async (assetId: string) => {
    if (!alarmId) return
    try {
      const updatedAlarm = await actions.v1.alarms.detachAlarmAsset({alarmId, assetId, siteId: state.site!.id!})
      setAlarm(prev =>
        prev
          ? {...prev, reason: formData.resolution, resolvedBy: formData.resolvedBy, assets: updatedAlarm?.assets}
          : prev
      )
    } catch (err) {
      console.error('Error detaching asset from alarm', err)
    }
  }
  const openAssetInNewTab = async (assetId: string) => {
    try {
      const d = await actions.getDownloadUrl(assetId)
      if (d && d.downloadURL) {
        window.open(d.downloadURL, '_blank', 'noopener')
      }
    } catch (err) {
      console.error('Error opening asset', err)
    }
  }
  const handleSaveDescriptionClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const description = formData.resolution
    try {
      await onSaveDescription?.(description)
      onClose?.()
      resetModal()
    } catch (err) {
      console.error(err, 'Error updating alarm description')
    }
  }
  // whether the resolution text contains any non-whitespace characters
  const hasNonEmptyResolution = (formData.resolution ?? '').trim().length > 0
  const hasNonEmptyResolvedBy = (formData.resolvedBy ?? '').trim().length > 0
  const isResolveDisabledByMandatoryResolvedBy = isResolvedByMandatoryForAllAlarms && !hasNonEmptyResolvedBy

  return (
    <StyledModal
      id="resolveAlarmModal"
      minHeight="424px"
      maxWidth={isBatteryAlarm ? '1000px' : '578px'}
      headerHeight="64px"
      isOpen={isOpen}
      onClose={submitting ? undefined : onResetAndClose}
      title={title}
      headerLeftItem={
        <Button buttonStyle="secondary" buttonSize="small" onClick={onResetAndClose} disabled={submitting}>
          {t('common:buttons.cancel', 'Cancel')}
        </Button>
      }
      headerRightItem={
        <Button
          buttonStyle="primary"
          buttonSize="small"
          onClick={onSubmit}
          // Require non-empty description (resolution) to enable Resolve button
          disabled={
            !hasNonEmptyResolution ||
            isResolveDisabledByMandatoryResolvedBy ||
            submitting ||
            (!alarm?.isResolvable && !isSuperUser) ||
            isHealthInspector
          }
          form="resolveAlarmModalForm"
          data-cy="resolve-alarm-modal-button-solve"
        >
          {t('common:buttons.resolve', 'Resolve')}
        </Button>
      }
      headerBackgroundColor={colors.system.red}
      {...rest}
    >
      <ScrollableBody $headerPx={64}>
        {isLoading || alarm === null ? (
          <LoadingWithPuff />
        ) : (
          <ContentContainer showInstructions={isBatteryAlarm}>
            {isBatteryAlarm && (
              <AlarmInstructions
                title={t('alarms:modal.instruction.title', 'Instructions')}
                content={getAlarmInstructions()}
              />
            )}
            <FormArea>
              <AlarmDetails
                alarm={alarm}
                t={t}
                unit={unit}
                minUnit={limitUnit}
                maxUnit={limitUnit}
                formattedValue={measuredValue}
                locale={locale}
              />
              {chefsteinTip && (
                <ChefsteinTip
                  expanded={isChefsteinTipExpanded}
                  onToggle={() => setIsChefsteinTipExpanded(expanded => !expanded)}
                  title={t('alarms:modal.chefsteinTip.title', 'Chefstein tip')}
                  closeLabel={t('common:buttons.close', 'Close')}
                  content={t(chefsteinTip.translationKey, chefsteinTip.fallback)}
                />
              )}
              <Form id="resolveAlarmModalForm">
                {hasActorSuggestions ? (
                  <InputWithTags
                    label={t('appliances:labels.resolvedBy', 'Resolved By')}
                    placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
                    savedSuggestions={formData.resolvedBy}
                    defaultSuggestions={actorSuggestions}
                    onChange={(_key, selected) => handleResolvedByChange(selected[0] ?? '')}
                    disabled={submitting || isHealthInspector}
                    required={isResolvedByMandatoryForAllAlarms}
                  />
                ) : (
                  <Input
                    id="resolveAlarmModalFormResolved"
                    name="resolveAlarmModalFormResolved"
                    label={t('appliances:labels.resolvedBy', 'Resolved By')}
                    placeholder={t('appliances:placeholders.resolvedBy', 'Enter name')}
                    onChange={onInputChange}
                    value={formData.resolvedBy}
                    disabled={submitting || isHealthInspector}
                    required={isResolvedByMandatoryForAllAlarms}
                  />
                )}
                <Input
                  id="resolveAlarmModalFormReason"
                  name="resolveAlarmModalFormReason"
                  label={t('appliances:labels.description', 'Description')}
                  placeholder={t('appliances:placeholders.resolution', 'Enter alarm reason & resolution')}
                  multiline
                  rows={6}
                  onChange={onInputChange}
                  value={formData.resolution}
                  disabled={submitting || isHealthInspector}
                  required
                />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {/* uploader next to save description */}
                    {/* hide uploader from Health Inspectors */}
                    {!isHealthInspector && !(alarm.assets && alarm.assets.length >= 3) && (
                      <AssetUploaderSmall onAssetSaved={handleAssetSaved} savedAsset={undefined} multiple={false} />
                    )}
                  </div>
                  <SaveButtonArea>
                    <Button
                      id="savebutton"
                      buttonStyle="primary"
                      buttonSize="small"
                      onClick={handleSaveDescriptionClick}
                      disabled={isHealthInspector}
                      data-cy="resolve-alarm-modal-button-solve"
                    >
                      {t('alarms:modal.buttons.save', 'Save')}
                    </Button>
                  </SaveButtonArea>
                </div>
              </Form>
              <Divider />
              <AssetsArea>
                {(() => {
                  const currentCount =
                    alarm.assets && Array.isArray(alarm.assets) && alarm.assets.length ? alarm.assets.length : 0
                  const uploaderVisible = !(alarm.assets && alarm.assets.length >= 3)
                  if (!uploaderVisible && currentCount === 0) return null
                  return (
                    <div style={{display: 'flex', alignItems: 'flex-start'}}>
                      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem'}}>
                        <AssetList>
                          {(alarm.assets || []).slice(0, 3).map(a => (
                            <AssetRow key={a.id}>
                              <ThumbnailContainer title={a.name} onClick={() => openAssetInNewTab(a.id)}>
                                <SmartAsset asset={a as any} />
                                <div className="remove-overlay">
                                  <IconButton
                                    icon={<IconCloseSmall />}
                                    buttonProps={{
                                      type: 'button',
                                      onMouseDown: (e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                      },
                                      onClick: (e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (!isHealthInspector) {
                                          handleRemoveAsset(a.id)
                                        }
                                      }
                                    }}
                                  />
                                </div>
                              </ThumbnailContainer>
                            </AssetRow>
                          ))}
                        </AssetList>
                        <UploadCounter>{`${currentCount}/3`}</UploadCounter>
                      </div>
                    </div>
                  )
                })()}
              </AssetsArea>
              {alarm.category === 'task' && alarm.type !== AlarmType.Cooling && (
                <Button
                  buttonStyle="primary"
                  buttonSize="small"
                  onClick={handleButtonClick}
                  disabled={alarm.followupTaskCreated || isHealthInspector}
                  data-cy="create-followup-task"
                >
                  {loadingTask ? (
                    <LoadingWithPuff />
                  ) : (
                    t('alarms:modal.buttons.createFollowupTask', 'Create Follow-Up Task')
                  )}
                </Button>
              )}
              {alarm.followupTaskCreated && (
                <TextInfo level={3} color={colors.system.green}>
                  {t('alarms:messages.createdFollowupTask', 'Follow-up task has been created.')}
                </TextInfo>
              )}
              {alarm.latestValue && (alarm.subtype === AlarmSubType.High || alarm.subtype === AlarmSubType.Low) && (
                <CurrentMeasurement t={t} language={language} alarm={alarm} unit={unit} formattedValue={latestValue} />
              )}
              {showApplianceLink && alarm.applianceId && (
                <ApplianceLinkContainer>
                  <ApplianceLink href={`/appliances/${alarm.applianceId}`}>
                    <Text size="S">{t('alarms:labels.applianceLink', 'View appliance details')}</Text>
                    <SharedIcon icon={SmallIconPalette.ArrowheadRight} width="0.75em" height="0.75em" />
                  </ApplianceLink>
                </ApplianceLinkContainer>
              )}
              {alarm.category === 'task' && <TaskDetails alarm={alarm} t={t} />}
            </FormArea>
          </ContentContainer>
        )}
      </ScrollableBody>
    </StyledModal>
  )
}

const InstructionArea = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  background-color: ${colors.system.lightGrey_5};
  border-radius: 1rem;
  min-height: 0;
  padding: 1.5rem;
`

interface AlarmInstructionsProps {
  title: string
  content: JSX.Element
}

const AlarmInstructions = ({title, content}: AlarmInstructionsProps) => {
  return (
    <InstructionArea>
      <Title level={5} strong data-cy="modal-alarm-instructions-title">
        {title}
      </Title>
      {content}
    </InstructionArea>
  )
}

interface AlarmDetailsProps {
  alarm: Alarm
  unit?: string
  maxUnit?: string
  minUnit?: string
  formattedValue?: string | number
  locale: string
  t: TFunction
}

const AlarmDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  border-bottom: 1px ${colors.system.grey_5} solid;
  padding-bottom: 1rem;
`

const AlarmDetails = ({alarm, t, unit, maxUnit, minUnit, locale, formattedValue}: AlarmDetailsProps) => {
  return (
    <AlarmDetailsContainer>
      <SharedIcon icon={alarmIconPicker(alarm.type)} width="3em" height="3em" fill={colors.brand.cobalt} />
      <div>
        <Text size="XS" color={colors.system.grey_50} textTransform="uppercase">
          {alarmSubtypeToTranslation(t, alarm.type, alarm.subtype)}
        </Text>
        <Title level={5} strong style={{paddingTop: '0.5rem'}}>
          {alarmNameFormatter(t, alarm.name, alarm.sensorRole)}
        </Title>
        <Text size="S">{dateValueFormatter(alarm.triggeredTime, locale)}</Text>
      </div>
      <ApplianceData
        value={formattedValue}
        unit={unit}
        minValue={alarm.limits?.minValue ?? undefined}
        maxValue={alarm.limits?.maxValue ?? undefined}
        minUnit={minUnit}
        maxUnit={maxUnit}
        hasAlarm
      />
    </AlarmDetailsContainer>
  )
}

interface ChefsteinTipProps {
  expanded: boolean
  onToggle: () => void
  title: string
  closeLabel: string
  content: string
}

const ChefsteinTip = ({expanded, onToggle, title, closeLabel, content}: ChefsteinTipProps) => {
  if (!expanded) {
    return (
      <ChefsteinTipButton
        type="button"
        onClick={onToggle}
        aria-expanded={false}
        aria-controls="chefstein-tip-content"
        data-cy="chefstein-tip-open"
      >
        <SharedIcon icon={SmallIconPalette.Tip} width="1.5rem" height="1.5rem" fill={colors.brand.purple} />
        <Text size="S" color={colors.brand.purple} strong>
          {title}
        </Text>
      </ChefsteinTipButton>
    )
  }

  return (
    <ChefsteinTipPanel id="chefstein-tip-content" data-cy="chefstein-tip-content">
      <ChefsteinTipHeader>
        <ChefsteinTipTitle>
          <SharedIcon icon={SmallIconPalette.Tip} width="1.5rem" height="1.5rem" fill={colors.brand.purple} />
          <Text size="S" color={colors.brand.purple} strong>
            {title}
          </Text>
        </ChefsteinTipTitle>
        <IconButton
          icon={<SharedIcon icon={SmallIconPalette.Close} width="1.25em" height="1.25em" fill={colors.brand.cobalt} />}
          buttonProps={{
            type: 'button',
            onClick: onToggle,
            'aria-label': `${closeLabel}: ${title}`,
            'aria-expanded': true,
            'aria-controls': 'chefstein-tip-content'
          }}
        />
      </ChefsteinTipHeader>
      <Text size="S">{content}</Text>
    </ChefsteinTipPanel>
  )
}

interface CurrentTemperatureProps {
  t: TFunction
  language: string
  alarm: Alarm
  unit?: string
  formattedValue?: string | number
}

const CurrentMeasurement: React.FC<CurrentTemperatureProps> = ({t, language, alarm, unit, formattedValue}) => {
  const title =
    alarm.type === AlarmType.Humidity
      ? t('common:labels.currentHumidity', 'Current humidity')
      : t('common:labels.currentTemperature', 'Current temperature')

  return (
    <CurrentTemperatureContainer>
      <CurrentTemperatureLabel>
        <Title level={5} strong>
          {title}
        </Title>
        {alarm.latestValue?.receivedAt && (
          <Text size="S">
            {t<string>('appliances:labels.lastUpdate', 'Last update {{ago}}', {
              ago: getDatetimeAgo(new Date(alarm.latestValue.receivedAt), language)
            })}
          </Text>
        )}
      </CurrentTemperatureLabel>
      {typeof alarm.latestValue?.value === 'number' && (
        <ApplianceDataValueAndUnit value={formattedValue} unit={unit} valueTextLevel={2} unitTextSize="S" />
      )}
    </CurrentTemperatureContainer>
  )
}
interface TaskDetailsProps {
  alarm: Alarm
  t: TFunction
}

const TaskDetails: React.FC<TaskDetailsProps> = ({t, alarm}) => {
  return (
    <TaskDetailsContainer>
      <Title level={5} strong>
        {t<string>('tasks:completed.taskDetails', 'Task details')}
      </Title>
      {alarm.category === 'task' && alarm.measurementTarget ? (
        <Column1>
          <Text size="S" color={colors.system.grey_50}>
            {t<string>('tasks:labels.measurementValueTarget', 'Measurement target')}
          </Text>
          <Text size="S">{alarmNameFormatter(t, alarm.measurementTarget, null)}</Text>
        </Column1>
      ) : null}
      <Column2>
        <Text size="S" color={colors.system.grey_50}>
          {t<string>('tasks:completed.notes', 'Note')}
        </Text>
        <Text size="S">{alarm.note}</Text>
      </Column2>
      <Column1>
        <Text size="S" color={colors.system.grey_50}>
          {t<string>('tasks:labels.columnNameCompletedBy', 'Completed by')}
        </Text>
        <Text size="S">{alarm.completedBy}</Text>
      </Column1>
    </TaskDetailsContainer>
  )
}

const StyledModal = styled(Modal)`
  h2 {
    font-size: 1.25rem;
    line-height: 1.5rem;
    color: ${colors.system.white};
  }
`
interface ContentContainerProps {
  showInstructions: boolean
}

const ScrollableBody = styled.div<{$headerPx: number}>`
  --overlay-top: 5rem;
  --overlay-bottom: 1rem;
  --header-h: ${p => p.$headerPx}px;

  max-height: calc(100dvh - var(--overlay-top) - var(--overlay-bottom) - var(--header-h));
  @supports not (height: 100dvh) {
    max-height: calc(100vh - var(--overlay-top) - var(--overlay-bottom) - var(--header-h));
  }

  overflow: auto;
`

const ContentContainer = styled.div<ContentContainerProps>`
  padding: 1rem 1.5rem;
  gap: 1rem;
  display: grid;
  grid-auto-flow: row;
  ${({showInstructions}) =>
    showInstructions &&
    css`
      justify-content: center;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 1.5rem;
      min-height: 0;
    `}
`

const FormArea = styled(InstructionArea)`
  background-color: ${colors.system.white};
`

const Form = styled.form`
  display: grid;
  grid-auto-flow: row;
  gap: 1rem;
  width: 100%;
`

const ChefsteinTipButton = styled.button`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${colors.brand.purple};
  border-radius: 0.5rem;
  background: ${colors.system.white};
  cursor: pointer;

  :focus-visible {
    outline: 2px solid ${colors.system.blue};
    outline-offset: 2px;
  }
`

const ChefsteinTipPanel = styled.section`
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${colors.brand.lavendel};
  border-radius: 0.5rem;
  background-color: ${colors.brand.lavendel_20};
`

const ChefsteinTipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const ChefsteinTipTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CurrentTemperatureContainer = styled.div`
  padding: 1.5rem;
  background-color: ${colors.system.lightGrey_5};
  border-radius: 0.5rem;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
`

const CurrentTemperatureLabel = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 0.125rem;
`

const ApplianceLinkContainer = styled.div`
  display: grid;
  justify-content: end;

  :hover {
    text-decoration: underline;
  }
`

// TODO: Using the react-navi Link doesn't do page reload but not sure if react-navi should be used in shared-frontend-components to make link component
const ApplianceLink = styled(Link)`
  text-decoration: none;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 0.5rem;

  :visited {
    color: ${colors.brand.cobalt};
  }
`

const TaskDetailsContainer = styled(CurrentTemperatureContainer)`
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto;
  grid-gap: 1rem;
  margin-bottom: 1rem;
  > h5 {
    grid-column: span 2;
  }
`

const Column1 = styled.div`
  grid-row: span 1;
  display: grid;
  grid-template-rows: fit-content;
  align-items: start;
  align-self: start;
  > span:first-of-type {
    padding-bottom: 0.2rem;
  }
`

const Column2 = styled(Column1)`
  grid-row: span 2;
`
const Divider = styled.hr`
  margin: 0;
  height: 1px;
  background-color: ${colors.system.grey_5};
  border: none;
`
const SaveButtonArea = styled.div`
  justify-self: end;
`

const AssetsArea = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const AssetUploaderRow = styled.div`
  display: flex;
  align-items: center;
`

const AssetList = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: inherit;
  overflow-x: auto;
  padding-top: 0.25rem;
`

const AssetRow = styled.div`
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;
  width: 150px;
  height: 125px;
  border: 1px solid;
  border-radius: 8px;
`

const ThumbnailContainer = styled.div`
  width: auto;
  height: 120px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  .remove-overlay {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 50;
  }
`

const UploadCounter = styled.div`
  font-size: 0.825rem;
  color: ${colors.system.grey_50};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.75rem;
  margin-top: 7px;
`
